// Shared look for the guided tour (spotlight card + welcome prompt).
// Injected once by TourProvider, styled to the app's purple palette and
// mirrored for the `html.dark` theme like the rest of the dashboard.
//
// NOTE on `!important`: src/style.css sets `p, h1..h6 { color:#000 !important }`
// globally. Without matching !important here the prompt's heading and copy render
// black on the purple header (invisible), and every line of card text renders
// black on the dark-mode surface. Any new text colour in this file needs it too.

const TourStyles = () => (
  <style>{`
    .ktour-mask{ position:fixed; background:rgba(13,10,36,.58); z-index:2000000;
      -webkit-backdrop-filter:blur(1.5px); backdrop-filter:blur(1.5px); }
    /* waiting for a target that isn't rendered yet — hint at the tour without
       hiding (or blocking) the page the user still has to act on */
    .ktour-mask.is-idle{ background:rgba(13,10,36,.3); -webkit-backdrop-filter:none; backdrop-filter:none; }
    .ktour-ring{ position:fixed; z-index:2000001; pointer-events:none; border-radius:12px;
      box-shadow:0 0 0 2px #8b78f0, 0 0 0 7px rgba(108,92,231,.3);
      animation:ktourPulse 2s ease-in-out infinite; }
    @keyframes ktourPulse{
      0%,100%{ box-shadow:0 0 0 2px #8b78f0, 0 0 0 7px rgba(108,92,231,.3); }
      50%{ box-shadow:0 0 0 2px #8b78f0, 0 0 0 12px rgba(108,92,231,.14); }
    }

    /* ---------- gesture demo (drag a region) ----------
       Loops a dashed box drawing itself under a travelling pointer. Purely
       decorative: pointer-events stay off so the user's real drag lands on the
       document underneath, which is the entire point of the step. */
    .ktour-gesture{ position:fixed; z-index:2000001; pointer-events:none; }
    .ktour-gesture-box{
      position:absolute; top:0; left:0; width:0; height:0; box-sizing:border-box;
      border:2px dashed #6c5ce7; border-radius:6px; background:rgba(108,92,231,.12);
      animation:ktourDragBox 2.8s ease-in-out infinite;
    }
    .ktour-gesture-dot{
      position:absolute; top:0; left:0; width:15px; height:15px; margin:-8px 0 0 -8px;
      border-radius:50%; background:#6c5ce7;
      box-shadow:0 0 0 5px rgba(108,92,231,.22), 0 2px 6px rgba(28,23,71,.4);
      animation:ktourDragDot 2.8s ease-in-out infinite;
    }
    /* The box grows from the press point to the release point, holds so the shape
       reads, then clears before the next pass. */
    @keyframes ktourDragBox{
      0%,8%    { width:0; height:0; opacity:0; }
      14%      { width:0; height:0; opacity:1; }
      52%      { width:100%; height:100%; opacity:1; }
      78%      { width:100%; height:100%; opacity:1; }
      92%,100% { width:100%; height:100%; opacity:0; }
    }
    /* Travels with the box's corner, and is only visible while "pressed". */
    @keyframes ktourDragDot{
      0%,8%    { left:0; top:0; opacity:0; }
      14%      { left:0; top:0; opacity:1; }
      52%      { left:100%; top:100%; opacity:1; }
      78%      { left:100%; top:100%; opacity:1; }
      92%,100% { left:100%; top:100%; opacity:0; }
    }
    @media (prefers-reduced-motion:reduce){
      /* Keep the demonstration, drop the movement: a static dashed box still shows
         the shape a drag produces. */
      .ktour-gesture-box{ animation:none; width:100%; height:100%; opacity:1; }
      .ktour-gesture-dot{ animation:none; opacity:0; }
    }

    /* ---------- click hint (press THIS control) ----------
       For steps whose spotlight covers a whole record — a grid row — where the
       ring alone doesn't say which of the row's controls to use. Decorative
       only: pointer-events stay off so the press lands on the real button. */
    .ktour-tap{ position:fixed; z-index:2000001; pointer-events:none; }
    .ktour-tap-ring{
      position:absolute; inset:-5px; border-radius:12px; box-sizing:border-box;
      border:2px solid #6c5ce7; background:rgba(108,92,231,.1);
      animation:ktourTapRing 1.6s ease-in-out infinite;
    }
    /* A pointer arrow that comes in, taps, and lifts — drawn in CSS so there is
       no image to load and it inherits the tour's purple. */
    .ktour-tap-cursor{
      position:absolute; left:50%; top:58%; width:16px; height:16px; margin:0 0 0 -2px;
      background:#2b2350; opacity:.92;
      filter:drop-shadow(0 2px 4px rgba(20,15,60,.45));
      clip-path:polygon(0 0, 0 76%, 22% 58%, 40% 100%, 58% 90%, 40% 50%, 72% 46%);
      animation:ktourTapCursor 1.6s ease-in-out infinite;
    }
    @keyframes ktourTapRing{
      0%,100%{ box-shadow:0 0 0 0 rgba(108,92,231,.35); }
      /* contracts on the "press" frame so ring and pointer read as one gesture */
      46%    { box-shadow:0 0 0 9px rgba(108,92,231,0); transform:scale(.97); }
      70%    { transform:scale(1); }
    }
    @keyframes ktourTapCursor{
      0%     { transform:translate(7px,9px); opacity:0; }
      22%    { transform:translate(0,0); opacity:.92; }
      42%    { transform:translate(-1px,-2px) scale(.9); opacity:.92; }
      58%    { transform:translate(0,0); opacity:.92; }
      88%,100%{ transform:translate(4px,6px); opacity:0; }
    }
    @media (prefers-reduced-motion:reduce){
      /* Still says "this control", without anything moving. */
      .ktour-tap-ring{ animation:none; box-shadow:0 0 0 4px rgba(108,92,231,.22); }
      .ktour-tap-cursor{ animation:none; opacity:.92; }
    }

    /* ---------- spotlight step card ---------- */
    .ktour-card{
      position:fixed; z-index:2000002; width:348px; max-width:calc(100vw - 24px);
      box-sizing:border-box; padding:18px 20px 16px; border-radius:16px;
      background:#fff; border:1px solid rgba(108,92,231,.18);
      box-shadow:0 28px 70px -24px rgba(18,13,58,.6);
      font-family:'Inter',-apple-system,sans-serif;
      animation:ktourIn .18s ease-out;
    }
    @keyframes ktourIn{ from{opacity:0; transform:translateY(6px);} to{opacity:1; transform:none;} }
    .ktour-arrow{ position:fixed; z-index:2000002; width:12px; height:12px; transform:rotate(45deg);
      background:#fff; border-left:1px solid rgba(108,92,231,.18); border-top:1px solid rgba(108,92,231,.18); }
    .ktour-arrow.is-below{ border:none; border-right:1px solid rgba(108,92,231,.18);
      border-bottom:1px solid rgba(108,92,231,.18); }

    .ktour-top{ display:flex; align-items:center; gap:10px; margin-bottom:9px; }
    .ktour-step{ display:inline-flex; align-items:center; gap:7px;
      font-size:10.5px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:#6c5ce7; }
    .ktour-step i{ display:grid; place-items:center; width:19px; height:19px; border-radius:6px;
      background:linear-gradient(135deg,#6c5ce7,#8b78f0); color:#fff; font-style:normal;
      font-size:11px; font-weight:700; letter-spacing:0; }

    .ktour-card h4{ margin:0 0 6px !important; font-size:15.5px !important; font-weight:700 !important;
      letter-spacing:-.2px; color:#15123a !important; }
    .ktour-card p{ margin:0 !important; font-size:12.8px; line-height:1.55; color:#54507a !important; }
    .ktour-hint{ display:flex; align-items:flex-start; gap:7px; margin-top:11px; padding:9px 10px;
      border-radius:10px; background:rgba(245,158,11,.1); border:1px solid rgba(245,158,11,.28);
      font-size:11.5px; line-height:1.45; color:#8a5a06; }
    .ktour-hint svg{ font-size:15px; flex:none; margin-top:1px; color:#d97706 !important; }

    /* Skip sits left (margin-right:auto), Next/Done right — and an action step,
       which has no Next, still keeps Skip on the left. */
    .ktour-foot{ display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:16px; }
    .ktour-btn{ font-family:'Inter',sans-serif; font-size:12.5px; font-weight:600; cursor:pointer;
      height:33px; padding:0 14px; border-radius:9px; border:1px solid transparent; transition:all .16s ease; }
    .ktour-btn.ghost{ background:#fff; border-color:rgba(21,18,58,.12); color:#54507a; }
    .ktour-btn.ghost:hover{ background:rgba(108,92,231,.07); border-color:rgba(108,92,231,.32); color:#6c5ce7; }
    .ktour-btn.primary{ background:linear-gradient(135deg,#6c5ce7,#8b78f0); color:#fff;
      box-shadow:0 8px 20px -10px rgba(108,92,231,.9); }
    .ktour-btn.primary:hover{ filter:brightness(1.06); }
    /* No underline: Skip is the quiet way out, and underlining it gave it the same
       weight as the card's real link (the sample-document offer). Underlined on
       hover only, so it still reads as something you can press. */
    .ktour-skip{ background:none; border:none; padding:0; margin-right:auto; cursor:pointer;
      font-family:'Inter',sans-serif; font-size:12.5px; font-weight:600; color:#6c5ce7;
      text-decoration:none; text-underline-offset:2px; }
    .ktour-skip:hover{ color:#5a48d0; text-decoration:underline; }

    /* ---------- welcome prompt (shown once per login) ---------- */
    .ktour-promptwrap{ position:fixed; inset:0; z-index:2000010; display:grid; place-items:center;
      padding:20px; background:rgba(11,8,32,.66);
      -webkit-backdrop-filter:blur(5px); backdrop-filter:blur(5px); animation:ktourFade .2s ease-out; }
    @keyframes ktourFade{ from{opacity:0;} to{opacity:1;} }
    .ktour-prompt{ width:560px; max-width:100%; border-radius:22px; overflow:hidden; background:#fff;
      border:1px solid rgba(255,255,255,.12);
      box-shadow:0 48px 110px -34px rgba(11,8,32,.78); font-family:'Inter',-apple-system,sans-serif;
      animation:ktourUp .26s cubic-bezier(.16,.84,.44,1); }
    @keyframes ktourUp{ from{opacity:0; transform:translateY(14px) scale(.985);} to{opacity:1; transform:none;} }

    .ktour-prompt-head{ position:relative; overflow:hidden; padding:26px 30px 24px; color:#fff;
      background-image:linear-gradient(118deg,#151041 0%,#2e2268 45%,#5b49cf 100%); }
    /* soft light source top-right so the flat gradient reads less like a block */
    .ktour-prompt-head::after{ content:''; position:absolute; top:-70px; right:-50px; width:250px; height:250px;
      border-radius:50%; background:radial-gradient(circle,rgba(171,155,237,.45) 0%,rgba(171,155,237,0) 70%); }
    .ktour-prompt-head > *{ position:relative; z-index:1; }
    .ktour-eyebrow{ display:inline-flex; align-items:center; gap:6px; margin-bottom:13px;
      padding:5px 11px; border-radius:100px; background:rgba(255,255,255,.14);
      border:1px solid rgba(255,255,255,.32); color:#fff !important;
      font-size:10px; font-weight:700; letter-spacing:1.2px; }
    .ktour-eyebrow svg{ font-size:13px; color:#d9cfff !important; }
    .ktour-prompt-head h3{ margin:0 0 9px !important; font-size:23px !important; font-weight:700 !important;
      line-height:1.2 !important; letter-spacing:-.5px; color:#ffffff !important;
      text-shadow:0 2px 18px rgba(8,5,30,.4); }
    .ktour-prompt-head p{ margin:0 !important; font-size:13.5px; line-height:1.55;
      color:rgba(255,255,255,.88) !important; max-width:430px; }

    .ktour-prompt-body{ padding:22px 30px 4px; }
    .ktour-what{ margin:0 0 14px !important; font-size:10.5px !important; font-weight:700 !important;
      letter-spacing:1.1px; text-transform:uppercase; color:#8a87a8 !important; }
    /* numbered timeline — a connector line makes the three phases read in order */
    .ktour-steplist{ position:relative; margin:0; padding:0 0 4px; list-style:none; }
    .ktour-steplist::before{ content:''; position:absolute; left:13px; top:16px; bottom:22px; width:2px;
      border-radius:2px; background:linear-gradient(180deg,rgba(108,92,231,.35),rgba(108,92,231,.08)); }
    .ktour-steplist li{ position:relative; display:flex; align-items:flex-start; gap:13px; padding-bottom:16px; }
    .ktour-steplist li:last-child{ padding-bottom:6px; }
    .ktour-steplist i{ position:relative; z-index:1; display:grid; place-items:center; flex:none;
      width:28px; height:28px; border-radius:9px; font-style:normal; font-size:12px; font-weight:700;
      color:#fff; background:linear-gradient(135deg,#6c5ce7,#8b78f0);
      box-shadow:0 6px 14px -7px rgba(108,92,231,.95); }
    .ktour-steptext{ min-width:0; padding-top:2px; }
    .ktour-steptext b{ display:block; font-size:13.5px; font-weight:600 !important; color:#1c1747;
      line-height:1.35; }
    .ktour-steptext span{ display:block; margin-top:2px; font-size:12px; line-height:1.45; color:#6b6790; }

    .ktour-prompt-foot{ display:flex; align-items:center; justify-content:space-between; gap:14px;
      padding:16px 30px 20px; margin-top:8px; border-top:1px solid rgba(21,18,58,.07); }
    .ktour-duration{ display:inline-flex; align-items:center; gap:6px; font-size:11.5px; font-weight:500; color:#8a87a8; }
    .ktour-duration svg{ font-size:14px; color:#a9a5c4 !important; }
    .ktour-prompt-actions{ display:flex; align-items:center; gap:9px; }
    .ktour-prompt-actions .ktour-btn{ height:42px; padding:0 22px; font-size:13.5px; border-radius:12px; }
    .ktour-prompt-actions .ktour-btn.primary{ display:inline-flex; align-items:center; gap:6px;
      box-shadow:0 14px 30px -12px rgba(108,92,231,.95); }
    @media (max-width:560px){
      .ktour-prompt-head{ padding:22px 20px 20px; }
      .ktour-prompt-head h3{ font-size:20px !important; }
      .ktour-prompt-body{ padding:18px 20px 2px; }
      .ktour-prompt-foot{ flex-direction:column; align-items:stretch; padding:14px 20px 18px; }
      .ktour-prompt-actions{ flex-direction:column-reverse; align-items:stretch; }
      .ktour-prompt-actions .ktour-btn.primary{ justify-content:center; }
    }

    /* ---------- header trigger ---------- */
    .kshell-tourbtn{ display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 13px;
      border-radius:11px; cursor:pointer; border:1px solid rgba(108,92,231,.3);
      background:rgba(108,92,231,.07); color:#5a48d0;
      font-family:'Inter',sans-serif; font-size:12.5px; font-weight:600; transition:all .16s ease; }
    .kshell-tourbtn:hover{ background:rgba(108,92,231,.14); border-color:rgba(108,92,231,.5); }
    .kshell-tourbtn svg{ font-size:18px; }
    @media (max-width:820px){ .kshell-tourbtn span{ display:none; } .kshell-tourbtn{ padding:0; width:38px; justify-content:center; } }

    /* ---------- header trigger: attention state ----------
       Shown briefly after sign-in (see TourProvider) to point out that the
       walkthrough is still available once the modal prompt has been dismissed.

       An expanding ring rather than a blink: flashing opacity on and off is a
       genuine accessibility hazard, and at header size it reads as a rendering
       glitch rather than an invitation. The ring grows OUTWARD from the button
       via box-shadow, so nothing in the header reflows while it plays. */
    @keyframes kshell-tourbtn-ring{
      0%   { box-shadow:0 0 0 0 rgba(108,92,231,.5), 0 0 0 0 rgba(108,92,231,.28); }
      70%  { box-shadow:0 0 0 9px rgba(108,92,231,0), 0 0 0 16px rgba(108,92,231,0); }
      100% { box-shadow:0 0 0 0 rgba(108,92,231,0), 0 0 0 0 rgba(108,92,231,0); }
    }
    /* A slow breath on the fill, kept well away from a hard on/off flash. */
    @keyframes kshell-tourbtn-breathe{
      0%,100% { background:rgba(108,92,231,.1); border-color:rgba(108,92,231,.42); }
      50%     { background:rgba(108,92,231,.2); border-color:rgba(108,92,231,.72); }
    }
    /* The compass leans side to side — motion the eye catches peripherally,
       which is the whole job here. */
    @keyframes kshell-tourbtn-nudge{
      0%,60%,100% { transform:rotate(0deg); }
      70%         { transform:rotate(-13deg); }
      85%         { transform:rotate(11deg); }
    }
    .kshell-tourbtn.is-attn{
      animation:kshell-tourbtn-ring 1.9s ease-out infinite,
                kshell-tourbtn-breathe 1.9s ease-in-out infinite;
    }
    .kshell-tourbtn.is-attn svg{ animation:kshell-tourbtn-nudge 1.9s ease-in-out infinite; }
    /* Hovering means they have found it — drop the animation so the button
       behaves like a button again under the cursor. */
    .kshell-tourbtn.is-attn:hover{ animation:none; }
    .kshell-tourbtn.is-attn:hover svg{ animation:none; }
    @media (prefers-reduced-motion:reduce){
      /* Keep the emphasis, drop the movement: a static ring still marks the
         button out for anyone who has asked not to be animated at. */
      .kshell-tourbtn.is-attn,
      .kshell-tourbtn.is-attn svg{ animation:none; }
      .kshell-tourbtn.is-attn{
        background:rgba(108,92,231,.18);
        border-color:rgba(108,92,231,.7);
        box-shadow:0 0 0 4px rgba(108,92,231,.16);
      }
    }

    /* ---------- dark mode ---------- */
    html.dark .ktour-card{ background:#1b1f27; border-color:rgba(255,255,255,.12);
      box-shadow:0 28px 70px -24px rgba(0,0,0,.85); }
    html.dark .ktour-card h4{ color:#f2f3f8 !important; }
    html.dark .ktour-card p{ color:rgba(255,255,255,.74) !important; }
    html.dark .ktour-arrow{ background:#1b1f27; border-color:rgba(255,255,255,.12); }
    html.dark .ktour-step{ color:#a99bf5; }
    html.dark .ktour-hint{ background:rgba(245,158,11,.12); border-color:rgba(245,158,11,.32); color:#fbc46a; }
    html.dark .ktour-btn.ghost{ background:rgba(255,255,255,.05); border-color:rgba(255,255,255,.14); color:#e7e9ee; }
    html.dark .ktour-btn.ghost:hover{ background:rgba(139,123,240,.2); border-color:rgba(139,123,240,.45); color:#fff; }
    html.dark .ktour-skip{ color:#a99bf5; }
    html.dark .ktour-skip:hover{ color:#c3b8fb; }
    html.dark .ktour-prompt{ background:#1b1f27; border-color:rgba(255,255,255,.08);
      box-shadow:0 48px 110px -34px rgba(0,0,0,.92); }
    html.dark .ktour-what{ color:rgba(255,255,255,.5) !important; }
    html.dark .ktour-steptext b{ color:#f2f3f8; }
    html.dark .ktour-steptext span{ color:rgba(255,255,255,.62); }
    html.dark .ktour-steplist::before{ background:linear-gradient(180deg,rgba(139,123,240,.5),rgba(139,123,240,.08)); }
    html.dark .ktour-prompt-foot{ border-top-color:rgba(255,255,255,.08); }
    html.dark .ktour-duration{ color:rgba(255,255,255,.55); }
    html.dark .ktour-duration svg{ color:rgba(255,255,255,.4) !important; }
    html.dark .kshell-tourbtn{ background:rgba(139,123,240,.16); border-color:rgba(139,123,240,.4); color:#c3b8fb; }
    html.dark .kshell-tourbtn:hover{ background:rgba(139,123,240,.28); color:#fff; }
    /* The light-mode ring is too dim to read against a dark header, so both
       animations are restated on the lighter violet. */
    @keyframes kshell-tourbtn-ring-dark{
      0%   { box-shadow:0 0 0 0 rgba(139,123,240,.6), 0 0 0 0 rgba(139,123,240,.34); }
      70%  { box-shadow:0 0 0 9px rgba(139,123,240,0), 0 0 0 16px rgba(139,123,240,0); }
      100% { box-shadow:0 0 0 0 rgba(139,123,240,0), 0 0 0 0 rgba(139,123,240,0); }
    }
    @keyframes kshell-tourbtn-breathe-dark{
      0%,100% { background:rgba(139,123,240,.18); border-color:rgba(139,123,240,.45); }
      50%     { background:rgba(139,123,240,.34); border-color:rgba(139,123,240,.8); }
    }
    html.dark .kshell-tourbtn.is-attn{
      animation:kshell-tourbtn-ring-dark 1.9s ease-out infinite,
                kshell-tourbtn-breathe-dark 1.9s ease-in-out infinite;
    }
    html.dark .kshell-tourbtn.is-attn:hover{ animation:none; }
    @media (prefers-reduced-motion:reduce){
      html.dark .kshell-tourbtn.is-attn{
        animation:none;
        background:rgba(139,123,240,.3);
        border-color:rgba(139,123,240,.75);
        box-shadow:0 0 0 4px rgba(139,123,240,.2);
      }
    }
  `}</style>
);

export default TourStyles;
